import type { Pointer, ScrollTarget, ScrollValue } from "./types";

/**
 * Get the relative horizontal scroll value of an element
 * @param {ScrollTarget} target The scroll target element
 * @returns {number} The relative horizontal scroll value
 */
export const getElementScrollLeftRelative = (target: ScrollTarget): number => {
    const maxScroll = target.scrollWidth - target.clientWidth;
    if (maxScroll === 0) {
        return 0;
    }
    return target.scrollLeft / maxScroll;
};

/**
 * Get the relative vertical scroll value of an element
 * @param {ScrollTarget} target The scroll target element
 * @returns {number} The relative vertical scroll value
 */
export const getElementScrollTopRelative = (target: ScrollTarget): number => {
    const maxScroll = target.scrollHeight - target.clientHeight;
    if (maxScroll === 0) {
        return 0;
    }
    return target.scrollTop / maxScroll;
};

/**
 * Get the calculated relative left and top scroll values for a given element
 * @param {HTMLElement} target
 * @returns {{left:number,top:number}} horizontal and vertical scroll values
 */
export const getElementScrollValueRelative = (target: ScrollTarget): ScrollValue => {
    return {
        left: getElementScrollLeftRelative(target),
        top: getElementScrollTopRelative(target)
    };
};

/**
 * Get the constrained relative left and top scroll values between 0 and 1 for a given element
 * @param {HTMLElement} target
 * @returns {{left:number,top:number}} horizontal and vertical scroll values between 0 and 1
 */
export const getElementScrollValueFixed = (target: ScrollTarget): ScrollValue => {
    return {
        left: Math.max(0, Math.min(1, getElementScrollLeftRelative(target))),
        top: Math.max(0, Math.min(1, getElementScrollTopRelative(target)))
    };
};

export const convertToElementScrollValue = (
    value: number,
    target: ScrollTarget,
    direction: "horizontal" | "vertical" = "vertical"
): ScrollValue => {
    let { scrollTop, scrollLeft } = target;
    if (direction === "horizontal") {
        scrollLeft = value * (target.scrollWidth - target.clientWidth);
    } else {
        scrollTop = value * (target.scrollHeight - target.clientHeight);
    }
    return {
        left: scrollLeft,
        top: scrollTop
    };
};

/**
 * Get the UI event screen coords
 * @param {MouseEvent|TouchEvent} event
 * @returns {{x:number,y:number}} The pointer screen coords
 */
export const getEventPointer = (event: unknown): Pointer => {
    const touchEvent = event as TouchEvent;
    if (touchEvent.touches) {
        return {
            x: touchEvent.touches[0].clientX,
            y: touchEvent.touches[0].clientY
        };
    }
    const mouseEvent = event as MouseEvent;
    if (typeof mouseEvent.clientX !== "undefined" && typeof mouseEvent.clientY !== "undefined") {
        return {
            x: mouseEvent.clientX,
            y: mouseEvent.clientY
        };
    }
    return {
        x: 0,
        y: 0
    };
};

/**
 * Calculate the horizontal scrollbar handle offset based on scrollbar bounds and `scrollValue`
 * @param {HTMLElement} bar
 * @param {HTMLElement} handle
 * @param {number} scrollValue Scroll value between 0 and 1
 * @returns {number} The horizontal scrollbar handle offset in pixels
 */
export const getHandleOffsetX = (bar: HTMLElement, handle: HTMLElement, scrollValue: number): number => {
    const maxOffsetX = bar.clientWidth - handle.clientWidth;
    return Math.max(0, Math.min(maxOffsetX, scrollValue * maxOffsetX));
};

/**
 * Calculate the vertical scrollbar handle offset based on scrollbar bounds and `scrollValue`
 * @param {HTMLElement} bar
 * @param {HTMLElement} handle
 * @param {number} scrollValue Scroll value between 0 and 1
 * @returns {number} The vertical scrollbar handle offset in pixels
 */
export const getHandleOffsetY = (bar: HTMLElement, handle: HTMLElement, scrollValue: number): number => {
    const maxOffsetY = bar.clientHeight - handle.clientHeight;
    return Math.max(0, Math.min(maxOffsetY, scrollValue * maxOffsetY));
};
